
import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {StepDocumentFlat, TestcaseDBCollection} from '@/models/testcase.model';
import SbToolbar from '@/components/stead/workspaces/stepbuilder/sbToolbar.vue';
import SbFlow from '@/components/stead/workspaces/stepbuilder/sbFlow.vue';
import {payloadStep} from '@/store/stead/step.store';
import {runnerState} from '@/store/stead/runner.store';
import {getPassportData} from '@/shared/local.storage.utils';
import SbSelectTerminalDialog from '@/components/stead/workspaces/stepbuilder/dialogboxes/sbSelectTerminalDialog.vue';
import RehearsalDialog from "@/components/stead/workspaces/shared/rehearsal/RehearsalDialog.vue";
import {createRush, getBuffer, setBuffer} from "@/axios/repository/buffer.axios";
import SbCreateRushDialog from "@/components/stead/workspaces/stepbuilder/dialogboxes/sbCreateRushDialog.vue";
import {IInsertRush} from "@/models/buffer.model";
import {ProjectDocument} from "@/models/project.model";
import {UserDocument} from "@/models/user.model";
import {StepLightDocument} from "@/models/step.model";
import {TObjectDocument} from "@/models/tobject.model";
import SbStepFile from "@/components/stead/workspaces/stepbuilder/sbStepFile.vue";

@Component({
  components: {
    SbStepFile,
    SbCreateRushDialog, RehearsalDialog, SbSelectTerminalDialog, SbFlow, SbToolbar
  }
})
export default class WorkspaceStepBuilderPage extends Vue {
  // Les props
  @Prop() public passport!: string

  // Les propriétés
  private colFlow: number = 0
  private stepCursor: number = -1
  private cursorStep: StepLightDocument | null = null
  private rangeSelection: number[] = []
  private showSnackbar: boolean = false
  private msgSnackbar: string = ''
  private loading: boolean = false
  private debug: boolean = true
  private showDialog: boolean = false
  private showRehearsal: boolean = false
  private stepBuffer: Omit<StepDocumentFlat, '_id'>[] | null = null
  private showRushDialog: boolean = false
  private insertData: { pos: number, tobj: TObjectDocument } | null = null

  // Les propriétés calculées
  private get tcid() {
    return getPassportData(this.passport)
  }

  private get TcDoc(): TestcaseDBCollection {
    return this.$store.state.testcase.testcase
  }

  private get theme() {
    return this.$store.state.testcase.theme
  }

  private get runner(): runnerState {
    return this.$store.state.runner
  }

  private get isConnected(): boolean {
    return this.runner.ioTerm.isConnected()
  }

  private get isViewer(): boolean | undefined {
    return this.$store.getters['current/userIsViewer']
  }

  private get project(): ProjectDocument | null {
    return this.$store.state.current.Project
  }

  private get user(): UserDocument | null {
    return this.$store.state.current.User
  }

  private get prjid(): string | null {
    return this.project ? this.project._id : null
  }

  private get userid(): string | null {
    return this.user ? this.user._id : null
  }

  private get steps(): StepLightDocument[] {
    return this.$store.state.step.steps
  }

  // Les hooks
  public mounted() {
    // console.log('mount')
    this.$store.commit('runner/stop')
    this.loadTcDoc()
  }

  // Les méthodes surveillées
  @Watch('tcid')
  private async changeTcid() {
    if (this.isViewer) {
      await this.$router.replace({name: 'testcase.workspace', params: {passport: this.passport}})
    } else {
      await this.loadTcDoc()
    }
  }

  @Watch('TcDoc')
  private async changeTcDoc() {
    // console.log('loadTestcase isViewer=', this.isViewer)
    if (this.TcDoc) {
      this.$store.commit('current/setWorkspace', {
        title: this.TcDoc.name,
        theme: this.$store.state.testcase.theme
      })
    }
  }

  @Watch('isViewer')
  private onViewer() {
    // this.loadTestcase()
  }

  @Watch('prjid')
  private async onPrjid() {
    if (this.prjid) {
      // Chargement dans le store de screen des packs des écrans
      await this.$store.dispatch('screen/loadPacks', this.prjid)
    }
  }

  @Watch('userid')
  private async onUserid() {
  }

  @Watch('stepCursor')
  private onChangeSetCursor() {
    this.insertData = null
  }

  // Les méthodes d'instance
  private async loadTcDoc() {
    const promises = [this.$store.dispatch('step/getTestcaseSteps', this.tcid)]
    if (this.tcid) {
      const alreadyLoadedTC = this.$store.state.testcase.testcase
      if (!alreadyLoadedTC || this.tcid !== alreadyLoadedTC._id) {
        promises.push(this.$store.dispatch('testcase/getTestcase', this.tcid))
      }

      this.loading = true
      await Promise.all(promises)
      this.loading = false
    }
  }

  private onClickTool(btn: string) {
    switch (btn) {
      case 'swap':
        this.swapPanels()
        break
      case 'copy':
        this.copyToBuffer()
        break
      case 'selectall':
        this.rangeSelection = [...this.steps.map((s, index) => index)]
        break
      case 'rush':
        this.showRushDialog = true
        break
      case 'paste':
        this.loading = true
        this.pasteBuffer().finally(() => {
          this.loading = false
        })
        break
      case 'cut':
        this.loading = true
        this.deleteSome().finally(() => {
          this.loading = false
          // this.copyToBuffer()
        })
        break
      case 'clear':
        this.rangeSelection = []
        this.copyToBuffer()
        break
      case 'run':
      case 'debug':
        this.debug = btn === 'debug'
        this.showDialog = true
        break
      case 'dbgstop':
        this.$store.commit('runner/stop')
        break
      case 'dbgnext':
        this.$store.commit('runner/dbgNext')
        break
      case 'dbgresume':
        this.$store.commit('runner/dbgResume')
        break
      case 'killbreakpoints':
        this.$store.commit('runner/killBreakpoints')
        break
      case 'rehearsal' :
        this.showRehearsal = true
        break
      case 'getbuffer' :
        this.getBuffer().then(() => {
          if (this.stepBuffer) {
            this.msgSnackbar = this.stepBuffer.length ? `${this.stepBuffer.length} steps copied in buffer` : 'Permanent buffer is empty'
            this.showSnackbar = true
          }
        })
        break
    }
  }

  private updateCursor() {
    if (this.insertData) {
      const pos = this.insertData.pos + 1
      this.stepCursor = pos > this.steps.length ? this.steps.length : pos
      this.insertData = null
    }
  }

  private async onInsertRush(rushSteps: IInsertRush) {
    await this.insertMoultSteps(rushSteps.pos, rushSteps.steps)
  }

  private onChanged() {
    // this.wStep = null
    // this.insertMode = false
    const sid = this.cursorStep
    this.cursorStep = null
    this.cursorStep = sid
  }

  private swapPanels() {
    this.colFlow = this.colFlow === 0 ? 1 : 0
  }

  private async copyToBuffer() {
    if (this.prjid && this.userid) {
      const buff = await setBuffer(this.prjid, this.userid, this.buffer(this.rangeSelection))
      if (buff) {
        this.msgSnackbar = this.rangeSelection.length ? `${this.rangeSelection.length} steps copied in buffer` : 'Clear buffer'
        this.showSnackbar = true
        this.rangeSelection = []
        this.stepBuffer = buff.steps
      } else {
        this.msgSnackbar = 'Failed to save buffer'
        this.showSnackbar = true
        this.stepBuffer = null
      }
    }
  }

  private async pasteBuffer() {
    // Si le stepBuffer existe ...
    if (this.stepBuffer) {
      await this.insertMoultSteps(this.stepCursor, this.stepBuffer)
    }
  }

  private async insertMoultSteps(pos: number, steps: Omit<StepDocumentFlat, '_id'>[]) {

    // Si le stepBuffer existe ...
    if (this.tcid && pos >= 0) {
      const stepId = pos < this.steps.length ? this.steps[this.stepCursor]._id : null
      const payload: payloadStep = {pos, tcid: this.tcid, steps}
      await this.$store.dispatch('step/insertStep', payload)
      const stepcursor = stepId ? this.steps.findIndex(s => s._id === stepId) : this.steps.length
      if (stepcursor >= 0) {
        this.stepCursor = stepcursor
      }
    } else if (this.tcid) {
      this.msgSnackbar = 'Select the insertion step before pasting'
      this.showSnackbar = true
    }
  }

  private async deleteSome() {
    if (this.tcid) {
      const range = [...this.rangeSelection]
      const stepId = this.stepCursor >= 0 && this.stepCursor < this.steps.length ? this.steps[this.stepCursor]._id : ''
      await this.copyToBuffer()
      await this.$store.dispatch('step/removeSomeSteps', {
        tcid: this.tcid,
        stepids: this.stepsIds(range)
      })
      // /!\ stepcursor sera égal à -1 si le curseur était sur un step supprimé
      const stepcursor = this.steps.findIndex(s => s._id === stepId)
      if (stepcursor >= 0) {
        this.stepCursor = stepcursor
      }
    }
  }

  private buffer(range: number[]): Omit<StepDocumentFlat, '_id'>[] {
    const stepRange: Omit<StepDocumentFlat, '_id'>[] = []
    range.sort((a, b) => a - b).forEach((pos) => {
      const step: StepLightDocument | null = this.tcid && this.steps ? this.steps[pos] : null
      if (step) {
        stepRange.push({
          instruc: step.instruc,
          tobject: step.tobject._id,
          parameters: step.parameters.map((pdoc) => {
            return {
              descId: pdoc.descId,
              value: pdoc.value
            }
          })
        })
      }
    })
    return stepRange
  }

  private stepsIds(range: number[]): string[] {
    const stepids: string[] = []
    range.sort((a, b) => a - b).forEach((pos) => {
      const step: StepLightDocument | null = this.steps[pos]
      if (step) {
        stepids.push(step._id)
      }
    })
    return stepids
  }

  /**
   * Récupération du buffer stocke en base s'il existe
   * @private
   */
  private async getBuffer() {
    if (this.prjid && this.userid) {
      const buff = await getBuffer(this.prjid, this.userid)
      this.stepBuffer = buff ? buff.steps.map(s => {
        return {
          instruc: s.instruc,
          tobject: s.tobject,
          parameters: s.parameters
        }
      }) : null
    }
  }

  private async onCreateRush(rushname: string) {
    const steps = this.buffer(this.rangeSelection)
    const fullname = this.user ? `${this.user.firstname} ${this.user.lastname}` : '(unknow user)'
    const d = new Date()
    const testname = this.TcDoc ? this.TcDoc.name : '(unknow test case)'
    const description = `<em>Rush with ${steps.length} steps, created by ${fullname} on ${d.toLocaleDateString()} at ${d.toLocaleTimeString()} from test case : ${testname}</em>`
    if (this.prjid) {
      const rush = await createRush(this.prjid, rushname, description, steps)
      this.msgSnackbar = !rush ? 'Failed to create rush' : `Rush '${rushname}' created`
      this.showSnackbar = true
    }
    this.showRushDialog = false
  }

  // Les méthodes statiques
}
